import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';

import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import Input from '../../shared/components/Input';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import loginImage from '../../assets/images/login.png';
import { useAuthForm } from '../../shared/hooks/useAuthForm';
import { AUTH_STACK } from '../../utils/screenNames';
import { resetAccountPassword } from '../../redux/actions/authActions';
import { passwordResetCompleted } from '../../utils/analytics';

/**
 * UpdatePassword Page - Refactored with Tailwind CSS
 * Final step in password reset flow - user creates new password
 */
function UpdatePassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, confirmationCode } = location?.state || {};

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validatePassword,
    validate,
    setError,
  } = useAuthForm({ password: '', confirmPassword: '' });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /**
   * Validate password confirmation match
   */
  const validatePasswordMatch = (confirmPassword) => {
    if (!confirmPassword) {
      return 'Please confirm your password';
    }
    if (confirmPassword !== values.password) {
      return 'Passwords do not match';
    }
    return '';
  };

  /**
   * Handle password reset
   */
  const handleResetPassword = async () => {
    // Validate both fields
    const isValid = validate({
      password: validatePassword,
      confirmPassword: validatePasswordMatch,
    });

    if (!isValid) return;

    if (!confirmationCode || confirmationCode.length !== 6) {
      setError('password', 'Invalid verification code');
      return;
    }

    setIsLoading(true);

    try {
      await resetAccountPassword(
        email,
        null,
        values.password,
        confirmationCode,
        () => {
          passwordResetCompleted({ method: email ? 'email' : 'phone' });
          setIsLoading(false);
          toast.success('Password reset successfully!');
          navigate(AUTH_STACK.LOGIN_OPTIONS, { state: { showEmailForm: true } });
        },
        () => {
          setIsLoading(false);
          setError('password', 'Failed to reset password. Please try again.');
        }
      );
    } catch (error) {
      setIsLoading(false);
      setError('password', 'An error occurred. Please try again.');
    }
  };

  if (isLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-app-bg">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 md:top-6 md:left-6 z-20 flex items-center gap-2 text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-proxima text-sm">Back</span>
      </button>

      {/* Main Content */}
      <AuthLayout
        heroImage={loginImage}
        title="Create new password"
        subtitle="Enter a new password below to change your password"
      >
        {/* New Password Input */}
        <div className="w-full">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            New Password
          </label>
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="New Password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            errorMessage={errors.password}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            }
          />
        </div>

        {/* Confirm Password Input */}
        <div className="w-full mt-4">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            Confirm Password
          </label>
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            placeholder="Re-enter new password"
            value={values.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.confirmPassword}
            errorMessage={errors.confirmPassword}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            }
          />
        </div>

        {/* Reset Password Button */}
        <Button
          variant="primary"
          size="lg"
          onClick={handleResetPassword}
          className="w-full h-11 bg-[#00E7FF] mt-5 text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          disabled={!values.password || !values.confirmPassword}
        >
          Reset Password
        </Button>

        {/* Back to Login Link */}
        <button
          onClick={() => navigate(AUTH_STACK.LOGIN_OPTIONS)}
          className="w-full text-center text-sm text-app-text-secondary hover:text-light-blue transition-colors font-proxima mt-4"
        >
          Back to login
        </button>
      </AuthLayout>
    </div>
  );
}

export default UpdatePassword;
